import { isNil } from 'lodash';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { EnvVariables } from 'src/shared/env-variables';
import { RoutesEnum } from 'src/shared/routes';

export const ChatbaseWidget: React.FC = () => {
  // Hooks.
  const location = useLocation();

  // useRefs.
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Constants.
  const CHATBASE_WINDOW_ID = 'chatbase-bubble-window';
  const CHATBASE_BUTTON_ID = 'chatbase-bubble-button';
  const HEADER_CONTAINER_ID = 'header-container';

  // useEffects.
  useEffect(() => {
    const toggleChatbaseButton = (display: string) => {
      const chatBubbleButton = document.getElementById(CHATBASE_BUTTON_ID);
      if (chatBubbleButton) chatBubbleButton.style.display = display;
    };

    toggleChatbaseButton('flex');
    return () => toggleChatbaseButton('none');
  }, []);

  useEffect(() => {
    // Do nothing if the Chatbase script ID is not configured for this environment.
    if (isNil(EnvVariables.chatbaseScriptId)) {
      return;
    }

    if (!document.getElementById(EnvVariables.chatbaseScriptId)) {
      const script = document.createElement('script');
      script.src = 'https://www.chatbase.co/embed.min.js';
      script.id = EnvVariables.chatbaseScriptId;
      // Load script asynchronously to avoid blocking rendering.
      script.async = true;
      document.body.appendChild(script);
    }

    // Ref: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver.
    const observer = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        mutation.addedNodes.forEach((node) => {
          // Chatbase chat window.
          if (node instanceof HTMLElement && node.id === CHATBASE_WINDOW_ID) {
            node.classList.add(CHATBASE_WINDOW_ID);
            handleChatWindowUpdate();
          }

          // Chatbase chat bubble button.
          if (node instanceof HTMLElement && node.id === CHATBASE_BUTTON_ID) {
            const chatBubbleButton = node;
            const header = document.getElementById(HEADER_CONTAINER_ID);
            const innerDiv = chatBubbleButton.querySelector('div');

            if (innerDiv) innerDiv.innerHTML = '';

            chatBubbleButton.classList.add(CHATBASE_BUTTON_ID);

            if (!isNil(header)) {
              const repositionChatBubble = () => {
                // Ref: https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect.
                const headerBounds = header.getBoundingClientRect();

                // Align chat bubble to the header container’s left edge, keeping 12px margin.
                chatBubbleButton.style.right = `${window.innerWidth - headerBounds.left + 12}px`;
              };

              repositionChatBubble();

              resizeObserverRef.current?.disconnect();

              const resizeObserver = new ResizeObserver(repositionChatBubble);
              resizeObserver.observe(header);
              resizeObserverRef.current = resizeObserver;
            }

            updateChatbaseButton(chatBubbleButton);

            // Chatbase re-renders button contents dynamically (e.g., icons).
            // This observer ensures our custom styles and icon cleanup reapply after changes.
            const buttonObserver = new MutationObserver(() =>
              updateChatbaseButton(chatBubbleButton),
            );

            buttonObserver.observe(chatBubbleButton, {
              childList: true,
              subtree: true,
            });
          }
        });
      });
    });

    // Start observing the target node for configured mutations.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      // Disconnect the MutationObserver so it no longer listens for DOM mutations.
      observer?.disconnect();

      // Disconnect the ResizeObserver if it exists to prevent memory leaks.
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    handleChatWindowUpdate();
  }, [location.pathname]);

  // Handlers.
  const handleChatWindowUpdate = () => {
    const pathname = location.pathname;
    const chatWindow = getChatWidget();

    if (chatWindow) {
      const isFooterOffset =
        pathname.includes(RoutesEnum.Map3D) || pathname === RoutesEnum.Home;

      chatWindow.classList.toggle('footer-offset', isFooterOffset);
    }
  };

  const getChatWidget = () =>
    document.getElementById(CHATBASE_WINDOW_ID) as HTMLElement | null;

  const toggleActiveState = (button: HTMLElement) => {
    const widget = getChatWidget();
    if (!widget) return;

    const isVisible = window.getComputedStyle(widget).display === 'flex';
    button.classList.toggle('active-state', isVisible);
  };

  const removeIcons = (button: HTMLElement) => {
    button.querySelectorAll('img, svg').forEach((icon) => icon.remove());
  };

  const updateChatbaseButton = (button: HTMLElement) => {
    toggleActiveState(button);
    removeIcons(button);
  };

  return null;
};
